import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;
  #onResize = () => this.#syncSplitLayout();

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateBadgeBlocks();
    this.#syncSplitLayout();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#onResize);

    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#onResize);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => {
    return document.hidden ? this.pause() : this.resume();
  };

  #hydrateBadgeBlocks() {
    const root = this.closest('.announcement-bar');
    if (!root) return;

    const badgesContainer = root.querySelector('.announcement-bar__badges');
    if (!badgesContainer) return;

    const badgeSources = this.querySelectorAll('.announcement-bar__badge-source');
    badgeSources.forEach((source) => {
      const badgeLink = source.querySelector('.announcement-bar__badge-link');
      if (badgeLink) badgesContainer.appendChild(badgeLink);
      source.remove();
    });

    const layout = root.querySelector('.announcement-bar__layout');
    const hasBadges = badgesContainer.querySelectorAll('.announcement-bar__badge-link').length > 0;
    const hasContacts = (root.querySelectorAll('.announcement-bar__contact-link').length ?? 0) > 0;
    if (layout && (hasBadges || hasContacts)) {
      layout.classList.add('announcement-bar__layout--split');
    }

    this.#syncSplitLayout();
  }

  #syncSplitLayout() {
    const root = this.closest('.announcement-bar');
    if (!root) return;

    const layout = root.querySelector('.announcement-bar__layout');
    const badgesContainer = root.querySelector('.announcement-bar__badges');
    const contactsContainer = root.querySelector('.announcement-bar__contact');
    if (!(layout instanceof HTMLElement)) return;

    const leftWidth = badgesContainer ? badgesContainer.getBoundingClientRect().width : 0;
    const rightWidth = contactsContainer ? contactsContainer.getBoundingClientRect().width : 0;
    const sideMax = Math.ceil(Math.max(leftWidth, rightWidth));

    layout.style.setProperty('--announcement-side-max', `${sideMax}px`);
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
